import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LockIcon from "@mui/icons-material/Lock";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CatalogService } from "@services/catalog/catalogService";
import { CATALOG_TRIGGER_LABELS } from "@services/catalog/commands/cmdListCatalogVersions";
import AggregateVersionsDrawer from "@/shared/components/versions/AggregateVersionsDrawer";
import HistoryButton from "@/shared/components/versions/HistoryButton";
import { AggregateVersion } from "@models/aggregateVersion.model";
import {
  CatalogCoreFieldSample,
  CatalogDimension,
  CatalogEntityType,
  CatalogMetric,
  CatalogMetricType,
  CatalogVertical,
  FIELD_CATEGORY_LABEL,
  CatalogUsage,
  ItemUsage,
} from "@models/catalog.model";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { StructurePanel, StructureKind } from "./authoring/StructurePanel";
import { VerticalDialog } from "./authoring/VerticalDialog";
import { EntityTypeDialog } from "./authoring/EntityTypeDialog";
import { MetricTypeDialog } from "./authoring/MetricTypeDialog";
import { DimensionDialog, DimensionEdit } from "./authoring/DimensionDialog";
import { MetricDialog } from "./authoring/MetricDialog";
import { UsageChip } from "./authoring/usage";
import { CoreFieldSampleDialog } from "./authoring/CoreFieldSampleDialog";

const BRAND = "#845adf";
const DEFAULT_VERTICAL = "education"; // 48/50 clients are EDU (per MPN-1161)

// Columns the user can sort by; all are string/boolean fields on CatalogMetric.
type SortKey =
  | "entity_type_code"
  | "attr_key"
  | "display_name"
  | "value_type_code"
  | "is_secure"
  | "searchable";

const HEAD_CELL = {
  fontWeight: 600,
  color: "#8c9097",
  fontSize: 12,
  textTransform: "uppercase" as const,
  letterSpacing: 0.3,
};

// Which authoring dialog is open, and the row being edited (null = create).
type DialogState =
  | { kind: "vertical"; item: CatalogVertical | null }
  | { kind: "entityType"; item: CatalogEntityType | null }
  | { kind: "metricType"; item: CatalogMetricType | null }
  | { kind: "dimension"; item: DimensionEdit | null }
  | { kind: "metric"; item: CatalogMetric | null }
  | { kind: "coreFieldSample"; item: CatalogCoreFieldSample | null }
  | null;

function compare(a: CatalogMetric, b: CatalogMetric, key: SortKey): number {
  const av = a[key];
  const bv = b[key];
  if (typeof av === "boolean" || typeof bv === "boolean") {
    return Number(av) - Number(bv);
  }
  return String(av).localeCompare(String(bv));
}

export default function CatalogBrowserPage() {
  useDocumentTitle("Tracked Metric Catalog");
  const svc = useMemo(() => new CatalogService(), []);

  const [verticals, setVerticals] = useState<CatalogVertical[]>([]);
  const [vertical, setVertical] = useState<string>(DEFAULT_VERTICAL);
  const [metrics, setMetrics] = useState<CatalogMetric[]>([]);
  const [dimensions, setDimensions] = useState<CatalogDimension[]>([]);
  const [entityTypes, setEntityTypes] = useState<CatalogEntityType[]>([]);
  const [metricTypes, setMetricTypes] = useState<CatalogMetricType[]>([]);
  const [coreFieldSamples, setCoreFieldSamples] = useState<CatalogCoreFieldSample[]>([]);
  // What a rename would break, per row (MPN-1781). Fetched AFTER the vertical renders — it is the
  // heavy call — so chips fill in late; null while pending.
  const [usage, setUsage] = useState<CatalogUsage | null>(null);
  // Fresh usage for the item being edited, fetched when its dialog opens (one item = ~5 queries).
  // The table's value stands in until it resolves.
  const [editUsage, setEditUsage] = useState<{ key: string; usage: ItemUsage } | null>(null);
  const [loading, setLoading] = useState(false);

  const [dialog, setDialog] = useState<DialogState>(null);
  const [toast, setToast] = useState<string | null>(null); // errors (e.g. blocked delete)

  // Catalog history (MPN-1335). Fetched when the drawer opens rather than alongside the page:
  // the history is only ever read deliberately, and most visits never open it.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<AggregateVersion[] | null>(null);

  // Search term lives in the URL (?q=) so a search is shareable / survives back-nav.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("entity_type_code");
  const [sortAsc, setSortAsc] = useState(true);

  const currentVertical = verticals.find((v) => v.code === vertical) ?? null;

  const loadVerticals = useCallback(
    () => svc.listVerticals().then((vs) => { setVerticals(vs); return vs; }),
    [svc],
  );

  // Load the vertical list once; default to EDU if present, else the first vertical.
  useEffect(() => {
    loadVerticals().then((vs) => {
      if (!vs.some((v) => v.code === DEFAULT_VERTICAL) && vs.length > 0) {
        setVertical(vs[0].code);
      }
    });
  }, [loadVerticals]);

  const loadVerticalData = useCallback(() => {
    if (!vertical) return;
    setLoading(true);
    Promise.all([
      svc.listMetrics(vertical),
      svc.listDimensions(vertical),
      svc.listEntityTypes(vertical),
      svc.listMetricTypes(),
      svc.listCoreFieldSamples(vertical),
    ])
      .then(([m, d, et, mt, cfs]) => {
        setMetrics(m);
        setDimensions(d);
        setEntityTypes(et);
        setMetricTypes(mt);
        setCoreFieldSamples(cfs);
      })
      .finally(() => setLoading(false));
  }, [svc, vertical]);

  // Usage rides behind the page load; a response for a vertical the user has since left is dropped.
  useEffect(() => {
    if (!vertical) return;
    let current = true;
    setUsage(null);
    svc.getUsage(vertical).then((u) => { if (current) setUsage(u); }).catch(() => {});
    return () => { current = false; };
  }, [svc, vertical]);

  // Usage can go stale between page load and the moment somebody edits, so refetch the one item
  // when its dialog opens. Keyed on kind:id so reopening the same row refetches too.
  const editKey =
    dialog && (dialog.kind === "metric" || dialog.kind === "dimension" || dialog.kind === "entityType") && dialog.item
      ? `${dialog.kind}:${dialog.item.id}`
      : null;
  useEffect(() => {
    if (!editKey || !vertical) return;
    const [kind, idText] = editKey.split(":");
    const id = Number(idText);
    const only = kind === "metric" ? { metric_id: id } : kind === "dimension" ? { dimension_id: id } : { entity_type_id: id };
    const bucket: keyof CatalogUsage = kind === "metric" ? "metrics" : kind === "dimension" ? "dimensions" : "entity_types";
    let current = true;
    svc.getUsage(vertical, only)
      .then((u) => { if (current && u[bucket][id]) setEditUsage({ key: editKey, usage: u[bucket][id] }); })
      .catch(() => {});
    return () => { current = false; };
  }, [svc, vertical, editKey]);

  const usageFor = (bucket: keyof CatalogUsage, kind: string, id: number | undefined): ItemUsage | undefined => {
    if (id == null) return undefined;
    if (editUsage && editUsage.key === `${kind}:${id}`) return editUsage.usage;
    return usage?.[bucket][id];
  };

  // Reload everything whenever the selected vertical changes.
  useEffect(() => {
    setEntityFilter("all");
    loadVerticalData();
  }, [loadVerticalData]);

  const openHistory = useCallback(async () => {
    if (!currentVertical) return;
    setHistoryOpen(true);
    // Always refetch on open. A version is minted by the edit the user just made in a dialog, and
    // several of those dialogs raise no event this page listens for — cached rows would show the
    // history as it was before their change and read as though nothing was recorded.
    setVersions(null);
    try {
      setVersions(await svc.listCatalogVersions(currentVertical.id));
    } catch {
      setVersions([]);
      setToast("Could not load the catalog history.");
    }
  }, [svc, currentVertical]);

  // Drop another vertical's history rather than showing it under this one's name.
  useEffect(() => {
    setVersions(null);
  }, [vertical]);

  const entityTypeNames = useMemo(() => {
    const seen = new Map<string, string>();
    entityTypes.forEach((t) => seen.set(t.code, t.name));
    metrics.forEach((m) => seen.set(m.entity_type_code, m.entity_type_name));
    return Array.from(seen, ([code, name]) => ({ code, name }));
  }, [entityTypes, metrics]);

  const setQuery = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("q", value);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  // Reveal is the whole point: match the query against every field an author would
  // recognize a metric by, so a would-be duplicate surfaces before it's created.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = metrics.filter((m) => {
      if (entityFilter !== "all" && m.entity_type_code !== entityFilter) return false;
      if (!q) return true;
      return [
        m.attr_key,
        m.display_name,
        m.description,
        m.entity_type_code,
        m.entity_type_name,
        m.value_type_code,
        m.dimension_keys.join(" "),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
    const sorted = [...filtered].sort((a, b) => {
      // Stable secondary sort by attr_key keeps rows predictable within a group.
      const primary = compare(a, b, sortKey);
      return (sortAsc ? primary : -primary) || a.attr_key.localeCompare(b.attr_key);
    });
    return sorted;
  }, [metrics, query, entityFilter, sortKey, sortAsc]);

  // --- authoring actions ---------------------------------------------------

  const openAdd = (kind: StructureKind) => {
    if (kind === "entityType") setDialog({ kind: "entityType", item: null });
    else if (kind === "metricType") setDialog({ kind: "metricType", item: null });
    else setDialog({ kind: "dimension", item: null });
  };

  // A dimension row carries reference_entity_type_code; the editor needs the FK id.
  const openEditDimension = (d: CatalogDimension) => {
    const refId = entityTypes.find((t) => t.code === d.reference_entity_type_code)?.id ?? null;
    setDialog({ kind: "dimension", item: { ...d, reference_entity_type_id: refId } });
  };

  const afterSave = () => {
    setDialog(null);
    loadVerticalData();
  };

  const afterVerticalSave = (saved: CatalogVertical) => {
    setDialog(null);
    loadVerticals().then(() => setVertical(saved.code)); // jump to the just-created/renamed vertical
  };

  const del = async (kind: StructureKind | "metric" | "coreFieldSample", id: number, label: string) => {
    if (!window.confirm(`Delete "${label}"? This can't be undone from here.`)) return;
    try {
      if (kind === "entityType") await svc.deleteEntityType(id);
      else if (kind === "metricType") await svc.deleteMetricType(id);
      else if (kind === "dimension") await svc.deleteDimension(id);
      else if (kind === "coreFieldSample") await svc.deleteCoreFieldSample(id);
      else await svc.deleteMetric(id);
      loadVerticalData();
    } catch (e) {
      setToast(e instanceof Error ? e.message : "Delete failed");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header. History sits in the top-right action group, the same place and treatment the
          field map's Versions button has — it is a page-level action, not one of the filters. */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 0.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Tracked Metric Catalog
          </Typography>
          <Typography variant="body2" sx={{ color: "#8c9097", mt: 0.5 }}>
            Every metric a vertical tracks. Search here before defining a new one — if it already
            exists, reuse it instead of creating a duplicate.
          </Typography>
        </Box>
        {currentVertical && (
          <HistoryButton onClick={openHistory} title="Catalog change history" />
        )}
      </Box>

      {/* Controls */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", my: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={verticals.length ? vertical : ""}
            onChange={(e) => setVertical(e.target.value)}
            displayEmpty
          >
            {verticals.map((v) => (
              <MenuItem key={v.code} value={v.code}>
                {v.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button size="small" startIcon={<AddIcon />} onClick={() => setDialog({ kind: "vertical", item: null })}
          sx={{ textTransform: "none" }}>
          New vertical
        </Button>
        {currentVertical && (
          <Tooltip title="Rename vertical">
            <IconButton size="small" aria-label="Rename vertical" onClick={() => setDialog({ kind: "vertical", item: currentVertical })}>
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <Select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
            <MenuItem value="all">All entity types</MenuItem>
            {entityTypeNames.map((et) => (
              <MenuItem key={et.code} value={et.code}>
                {et.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search metrics…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 280, flexGrow: 1, maxWidth: 460 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#8c9097" }} />
              </InputAdornment>
            ),
          }}
        />

        <Typography variant="body2" sx={{ color: "#8c9097", ml: "auto" }}>
          {loading ? "Loading…" : `${visible.length} of ${metrics.length} metric${metrics.length === 1 ? "" : "s"}`}
        </Typography>
      </Box>

      {/* Structure — entity types + metric types (authoring). */}
      {currentVertical && (
        <StructurePanel
          entityTypes={entityTypes}
          metricTypes={metricTypes}
          verticalId={currentVertical.id}
          entityTypeUsage={usage?.entity_types}
          onAdd={openAdd}
          onEditEntityType={(t) => setDialog({ kind: "entityType", item: t })}
          onEditMetricType={(t) => setDialog({ kind: "metricType", item: t })}
          onDelete={(kind, id, label) => del(kind, id, label)}
        />
      )}

      {/* Dimensions — the tracking axes and the concrete values seen in the data. */}
      <Box sx={{ mb: 2, p: 2, bgcolor: "#fff", borderRadius: 2, border: "1px solid #f3f3f3" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={HEAD_CELL}>Dimensions</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setDialog({ kind: "dimension", item: null })}
            sx={{ ml: "auto", textTransform: "none" }}>
            Dimension
          </Button>
        </Box>
        {dimensions.length === 0 && <Box sx={{ color: "#c4c4c4", fontSize: 13 }}>None yet</Box>}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {dimensions.map((d) => (
            <Box key={d.id} sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap",
              "&:hover .dim-actions": { opacity: 1 } }}>
              <Box sx={{ minWidth: 150 }}>
                <Box sx={{ fontWeight: 600, fontSize: 13 }}>
                  {d.name}{" "}
                  <Box component="span" sx={{ fontFamily: "monospace", color: "#8c9097", fontWeight: 400 }}>
                    {d.code}
                  </Box>
                </Box>
                {d.reference_entity_type_code && (
                  <Box sx={{ color: "#8c9097", fontSize: 11 }}>ref: {d.reference_entity_type_code}</Box>
                )}
              </Box>
              <UsageChip usage={usage?.dimensions[d.id]} />
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {d.values.length > 0 ? (
                  d.values.map((v) => (
                    <Chip
                      key={v}
                      label={v}
                      size="small"
                      sx={{ bgcolor: "#f0ebfb", color: BRAND, fontSize: 11, height: 20 }}
                    />
                  ))
                ) : (
                  <Box sx={{ color: "#c4c4c4", fontSize: 12 }}>no values in data yet</Box>
                )}
              </Box>
              <Box className="dim-actions" sx={{ ml: "auto", opacity: 0, transition: "opacity .15s" }}>
                <Tooltip title="Edit">
                  <IconButton size="small" aria-label="Edit" onClick={() => openEditDimension(d)}>
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" aria-label="Delete" onClick={() => del("dimension", d.id, d.code)}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Key & contact samples — per-vertical doc-copy for core fields (MPN-1233). */}
      <Box sx={{ mb: 2, p: 2, bgcolor: "#fff", borderRadius: 2, border: "1px solid #f3f3f3" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={HEAD_CELL}>Key &amp; contact samples</Typography>
          <Button size="small" startIcon={<AddIcon />} disabled={!currentVertical}
            onClick={() => setDialog({ kind: "coreFieldSample", item: null })}
            sx={{ ml: "auto", textTransform: "none" }}>
            Sample
          </Button>
        </Box>
        <Typography sx={{ color: "#8c9097", fontSize: 12, mb: 1 }}>
          The example + description shown for the identity key and each contact channel in the file
          instructions. Set once per vertical — configs no longer re-enter these.
        </Typography>
        {coreFieldSamples.length === 0 && <Box sx={{ color: "#c4c4c4", fontSize: 13 }}>None yet</Box>}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {coreFieldSamples.map((s) => (
            <Box key={s.id} sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap",
              "&:hover .cfs-actions": { opacity: 1 } }}>
              <Chip label={s.role} size="small"
                sx={{ bgcolor: "#f0ebfb", color: BRAND, fontSize: 11, height: 20, minWidth: 56 }} />
              <Box sx={{ fontFamily: "monospace", fontSize: 13, minWidth: 140 }}>
                {s.sample || <span style={{ color: "#c4c4c4" }}>no sample</span>}
              </Box>
              <Box sx={{ color: "#555", fontSize: 13 }}>
                {s.description || <span style={{ color: "#c4c4c4" }}>—</span>}
              </Box>
              <Box className="cfs-actions" sx={{ ml: "auto", opacity: 0, transition: "opacity .15s" }}>
                <Tooltip title="Edit">
                  <IconButton size="small" aria-label="Edit" onClick={() => setDialog({ kind: "coreFieldSample", item: s })}>
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" aria-label="Delete" onClick={() => del("coreFieldSample", s.id, s.role)}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Metrics */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography sx={HEAD_CELL}>Metrics</Typography>
        <Button size="small" startIcon={<AddIcon />}
          disabled={!currentVertical || entityTypes.length === 0}
          onClick={() => setDialog({ kind: "metric", item: null })}
          sx={{ ml: "auto", textTransform: "none" }}>
          New metric
        </Button>
      </Box>
      <TableContainer sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f3f3f3" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f9fafb" }}>
              <TableCell sx={HEAD_CELL} sortDirection={sortKey === "entity_type_code" ? (sortAsc ? "asc" : "desc") : false}>
                <TableSortLabel
                  active={sortKey === "entity_type_code"}
                  direction={sortKey === "entity_type_code" && !sortAsc ? "desc" : "asc"}
                  onClick={() => toggleSort("entity_type_code")}
                >
                  Entity Type
                </TableSortLabel>
              </TableCell>
              <TableCell sx={HEAD_CELL}>
                <TableSortLabel
                  active={sortKey === "attr_key"}
                  direction={sortKey === "attr_key" && !sortAsc ? "desc" : "asc"}
                  onClick={() => toggleSort("attr_key")}
                >
                  Metric
                </TableSortLabel>
              </TableCell>
              <TableCell sx={HEAD_CELL}>Category</TableCell>
              <TableCell sx={HEAD_CELL}>Description</TableCell>
              <TableCell sx={HEAD_CELL}>
                <TableSortLabel
                  active={sortKey === "value_type_code"}
                  direction={sortKey === "value_type_code" && !sortAsc ? "desc" : "asc"}
                  onClick={() => toggleSort("value_type_code")}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell sx={HEAD_CELL}>Dimensions</TableCell>
              <TableCell sx={HEAD_CELL} align="center">PII</TableCell>
              <TableCell sx={HEAD_CELL}>Usage</TableCell>
              <TableCell sx={HEAD_CELL} align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((m) => (
              <TableRow key={m.id} sx={{ "&:last-child td": { borderBottom: 0 }, "&:hover .metric-actions": { opacity: 1 } }}>
                <TableCell sx={{ color: "#555", fontSize: 13 }}>{m.entity_type_name}</TableCell>
                <TableCell>
                  <Box sx={{ fontWeight: 600, fontFamily: "monospace", fontSize: 13 }}>{m.attr_key}</Box>
                  {m.display_name && (
                    <Box sx={{ color: "#8c9097", fontSize: 12 }}>{m.display_name}</Box>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={FIELD_CATEGORY_LABEL[m.category]}
                    size="small"
                    sx={{ bgcolor: "#eef1f5", color: "#555", fontSize: 11, height: 20 }}
                  />
                </TableCell>
                <TableCell sx={{ color: "#555", fontSize: 13, maxWidth: 340 }}>
                  {m.description || <span style={{ color: "#c4c4c4" }}>—</span>}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  <Box sx={{ fontWeight: 500 }}>{m.value_type_code}</Box>
                  <Box sx={{ color: "#8c9097", fontSize: 12 }}>{m.base_kind}</Box>
                  {m.allowed_values.length > 0 && (
                    <Tooltip title={m.allowed_values.join(", ")}>
                      <Box sx={{ color: BRAND, fontSize: 11, cursor: "default" }}>
                        {m.allowed_values.length} allowed values
                      </Box>
                    </Tooltip>
                  )}
                  {m.validator && (
                    <Box sx={{ color: "#8c9097", fontSize: 11 }}>validator: {m.validator}</Box>
                  )}
                </TableCell>
                <TableCell>
                  {m.dimension_keys.length > 0 ? (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {m.dimension_keys.map((d) => (
                        <Chip
                          key={d}
                          label={d}
                          size="small"
                          sx={{ bgcolor: "#f0ebfb", color: BRAND, fontSize: 11, height: 20 }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <span style={{ color: "#c4c4c4" }}>—</span>
                  )}
                </TableCell>
                <TableCell align="center">
                  {m.is_secure && (
                    <Tooltip title="PII — stored encrypted">
                      <LockIcon sx={{ fontSize: 16, color: "#8c9097" }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <UsageChip usage={usage?.metrics[m.id]} />
                </TableCell>
                <TableCell align="right">
                  <Box className="metric-actions" sx={{ opacity: 0, transition: "opacity .15s", whiteSpace: "nowrap" }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" aria-label="Edit" onClick={() => setDialog({ kind: "metric", item: m })}>
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" aria-label="Delete" onClick={() => del("metric", m.id, m.attr_key)}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!loading && visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", color: "#8c9097", py: 4 }}>
                  {metrics.length === 0 ? "No metrics defined yet." : `No metrics match “${query}”.`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Authoring dialogs */}
      {dialog?.kind === "vertical" && (
        <VerticalDialog initial={dialog.item} onClose={() => setDialog(null)} onSaved={afterVerticalSave} />
      )}
      {dialog?.kind === "entityType" && currentVertical && (
        <EntityTypeDialog
          verticalId={currentVertical.id}
          entityTypes={entityTypes}
          usage={usageFor("entity_types", "entityType", dialog.item?.id)}
          initial={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={afterSave}
        />
      )}
      {dialog?.kind === "metricType" && currentVertical && (
        <MetricTypeDialog
          verticalId={currentVertical.id}
          initial={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={afterSave}
        />
      )}
      {dialog?.kind === "dimension" && currentVertical && (
        <DimensionDialog
          verticalId={currentVertical.id}
          entityTypes={entityTypes}
          usage={usageFor("dimensions", "dimension", dialog.item?.id)}
          initial={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={afterSave}
        />
      )}
      {dialog?.kind === "metric" && currentVertical && (
        <MetricDialog
          verticalId={currentVertical.id}
          entityTypes={entityTypes}
          metricTypes={metricTypes}
          dimensions={dimensions}
          usage={usageFor("metrics", "metric", dialog.item?.id)}
          initial={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={afterSave}
        />
      )}
      {dialog?.kind === "coreFieldSample" && currentVertical && (
        <CoreFieldSampleDialog
          verticalId={currentVertical.id}
          existingRoles={coreFieldSamples.map((s) => s.role)}
          initial={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={afterSave}
        />
      )}

      <AggregateVersionsDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        versions={versions ?? []}
        title="Catalog history"
        subtitle={currentVertical?.name}
        emptyMessage={
          versions === null
            ? "Loading…"
            : "No history yet. Editing the catalog creates the first version."
        }
        triggerLabels={CATALOG_TRIGGER_LABELS}
      />

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="error" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
